import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, List, CheckSquare, Map, MapPin, Clock, Car, Bed, FileText, Download, CheckCircle2, AlertCircle, DollarSign } from 'lucide-react';
type TabType = 'calendar' | 'shows' | 'advancing' | 'itinerary';
export function SchedulePage() {
  const [activeTab, setActiveTab] = useState<TabType>('calendar');
  // Generate a mock calendar grid for June 2026
  // June 2026 starts on a Monday. 30 days.
  const days = Array.from({
    length: 35
  }, (_, i) => {
    const dayNum = i + 1;
    if (dayNum > 30) return {
      day: dayNum - 30,
      isNextMonth: true
    };
    // Mock data assignment
    if (dayNum === 10 || dayNum === 13 || dayNum === 17) return {
      day: dayNum,
      type: 'travel'
    };
    if (dayNum === 12) return {
      day: dayNum,
      type: 'confirmed',
      venue: 'The Empty Bottle',
      city: 'Chicago'
    };
    if (dayNum === 14) return {
      day: dayNum,
      type: 'hold',
      venue: 'Beachland Tavern',
      city: 'Cleveland'
    };
    if (dayNum === 18) return {
      day: dayNum,
      type: 'hold',
      venue: 'Higher Ground',
      city: 'Burlington'
    };
    if (dayNum === 20) return {
      day: dayNum,
      type: 'deadline',
      venue: 'The Empty Bottle',
      note: 'Deposit Due'
    };
    if (dayNum >= 22 && dayNum <= 25) return {
      day: dayNum,
      type: 'confirmed',
      venue: 'Various',
      city: 'Northeast'
    };
    return {
      day: dayNum,
      type: 'open'
    };
  });
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-7xl mx-auto flex flex-col h-[calc(100vh-8rem)]" data-id="element-4220">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 shrink-0" data-id="element-4221">
        <div className="flex items-center gap-4" data-id="element-4222">
          <h2 className="text-2xl font-bold" data-id="element-4223">June 2026</h2>
          <div className="flex items-center gap-1 bg-surface border border-border rounded-lg p-1" data-id="element-4224">
            <button className="p-1 hover:bg-surfaceHover rounded text-textMuted hover:text-textMain transition-colors" data-id="element-4225">
              <ChevronLeft className="w-5 h-5" data-id="element-4226" />
            </button>
            <button className="px-3 py-1 text-sm font-medium" data-id="element-4227">Today</button>
            <button className="p-1 hover:bg-surfaceHover rounded text-textMuted hover:text-textMain transition-colors" data-id="element-4228">
              <ChevronRight className="w-5 h-5" data-id="element-4229" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-surface border border-border rounded-lg p-1" data-id="element-4230">
          <button className="px-3 py-1.5 bg-background shadow-sm rounded text-sm font-medium" data-id="element-4231">
            Month
          </button>
          <button className="px-3 py-1.5 text-textMuted hover:text-textMain rounded text-sm font-medium transition-colors" data-id="element-4232">
            List
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-border mb-6 shrink-0" data-id="element-4233">
        <button onClick={() => setActiveTab('calendar')} className={`pb-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'calendar' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-4234">
          <CalendarIcon className="w-4 h-4" data-id="element-4235" /> Calendar
        </button>
        <button onClick={() => setActiveTab('shows')} className={`pb-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'shows' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-4236">
          <List className="w-4 h-4" data-id="element-4237" /> Shows
        </button>
        <button onClick={() => setActiveTab('advancing')} className={`pb-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'advancing' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-4238">
          <CheckSquare className="w-4 h-4" data-id="element-4239" /> Advancing
        </button>
        <button onClick={() => setActiveTab('itinerary')} className={`pb-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'itinerary' ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-4240">
          <Map className="w-4 h-4" data-id="element-4241" /> Itinerary
        </button>
      </div>

      {/* Calendar Tab */}
      {activeTab === 'calendar' && <>
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mb-4 text-xs shrink-0" data-id="element-4242">
            <div className="flex items-center gap-1.5" data-id="element-4243">
              <div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/50" data-id="element-4244"></div>{' '}
              Confirmed
            </div>
            <div className="flex items-center gap-1.5" data-id="element-4245">
              <div className="w-3 h-3 rounded bg-amber-500/20 border border-amber-500/50" data-id="element-4246"></div>{' '}
              Hold
            </div>
            <div className="flex items-center gap-1.5" data-id="element-4247">
              <div className="w-3 h-3 rounded bg-red-500/20 border border-red-500/50" data-id="element-4248"></div>{' '}
              Deadline
            </div>
            <div className="flex items-center gap-1.5" data-id="element-4249">
              <div className="w-3 h-3 rounded bg-surface border border-border" data-id="element-4250"></div>{' '}
              Travel
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="flex-1 bg-surface border border-border rounded-xl overflow-hidden flex flex-col" data-id="element-4251">
            {/* Days of week */}
            <div className="grid grid-cols-7 border-b border-border bg-background/50 shrink-0" data-id="element-4252">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => <div key={day} className="py-3 text-center text-xs font-semibold text-textMuted uppercase tracking-wider" data-id="element-4253">
                  {day}
                </div>)}
            </div>

            {/* Days grid */}
            <div className="flex-1 grid grid-cols-7 grid-rows-5" data-id="element-4254">
              {days.map((d, i) => <div key={i} className={`border-r border-b border-border p-2 flex flex-col gap-1 ${d.isNextMonth ? 'bg-background/30 opacity-50' : 'bg-background hover:bg-surface/50 transition-colors cursor-pointer'}`} data-id="element-4255">
                  <div className={`text-sm font-medium w-6 h-6 flex items-center justify-center rounded-full ${d.day === 6 && !d.isNextMonth ? 'bg-primary text-background' : 'text-textMuted'}`} data-id="element-4256">
                    {d.day}
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-1" data-id="element-4257">
                    {d.type === 'confirmed' && <div className="px-2 py-1 bg-green-500/10 border border-green-500/20 text-green-500 rounded text-xs truncate" data-id="element-4258">
                        <span className="font-semibold" data-id="element-4259">{d.venue}</span>
                        <div className="text-[10px] opacity-80 truncate" data-id="element-4260">
                          {d.city}
                        </div>
                      </div>}
                    {d.type === 'hold' && <div className="px-2 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-500 rounded text-xs truncate" data-id="element-4261">
                        <span className="font-semibold" data-id="element-4262">Hold:</span> {d.venue}
                      </div>}
                    {d.type === 'deadline' && <div className="px-2 py-1 bg-red-500/10 border border-red-500/20 text-red-500 rounded text-xs truncate" data-id="element-4263">
                        <span className="font-semibold" data-id="element-4264">{d.note}</span>
                      </div>}
                    {d.type === 'travel' && <div className="px-2 py-1 bg-surface border border-border text-textMuted rounded text-xs italic truncate" data-id="element-4265">
                        Travel day
                      </div>}
                    {d.type === 'open' && !d.isNextMonth && <div className="w-1.5 h-1.5 rounded-full bg-border mx-auto mt-2" data-id="element-4266"></div>}
                  </div>
                </div>)}
            </div>
          </div>
        </>}

      {/* Shows Tab */}
      {activeTab === 'shows' && <div className="flex-1 overflow-y-auto" data-id="element-4267">
          <div className="bg-surface border border-border rounded-xl overflow-hidden" data-id="element-4268">
            <table className="w-full text-sm text-left" data-id="element-4269">
              <thead className="text-xs text-textMuted uppercase bg-background/50 border-b border-border" data-id="element-4270">
                <tr data-id="element-4271">
                  <th className="px-6 py-4 font-medium" data-id="element-4272">Date</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4273">Venue & City</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4274">Status</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4275">Advance Progress</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4276">Lineup</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4277">Contract & Deposit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border" data-id="element-4278">
                {[{
              date: 'Jun 12',
              venue: 'The Empty Bottle',
              city: 'Chicago, IL',
              status: 'Confirmed',
              advance: 100,
              lineup: 'Locked',
              contract: 'Signed',
              deposit: 'Paid'
            }, {
              date: 'Jun 14',
              venue: 'Beachland Tavern',
              city: 'Cleveland, OH',
              status: 'Confirmed',
              advance: 60,
              lineup: 'Locked',
              contract: 'Signed',
              deposit: 'Pending'
            }, {
              date: 'Jun 18',
              venue: 'Higher Ground',
              city: 'Burlington, VT',
              status: 'Hold',
              advance: 0,
              lineup: 'Pending',
              contract: 'Draft',
              deposit: 'N/A'
            }, {
              date: 'Jun 22',
              venue: 'The Sinclair',
              city: 'Cambridge, MA',
              status: 'Confirmed',
              advance: 20,
              lineup: 'Locked',
              contract: 'Signed',
              deposit: 'Paid'
            }, {
              date: 'Jun 24',
              venue: 'Bowery Ballroom',
              city: 'New York, NY',
              status: 'Offer',
              advance: 0,
              lineup: 'Checking',
              contract: 'N/A',
              deposit: 'N/A'
            }].map((show, i) => <tr key={i} className="hover:bg-surfaceHover/50 transition-colors cursor-pointer" data-id="element-4279">
                    <td className="px-6 py-4 font-medium" data-id="element-4280">{show.date}</td>
                    <td className="px-6 py-4" data-id="element-4281">
                      <div className="font-medium" data-id="element-4282">{show.venue}</div>
                      <div className="text-xs text-textMuted" data-id="element-4283">{show.city}</div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4284">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${show.status === 'Confirmed' ? 'bg-green-500/10 text-green-500' : show.status === 'Hold' ? 'bg-amber-500/10 text-amber-500' : 'bg-primary/10 text-primary'}`} data-id="element-4285">
                        {show.status}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-4286">
                      <div className="flex items-center gap-2" data-id="element-4287">
                        <div className="w-24 h-1.5 bg-background rounded-full overflow-hidden" data-id="element-4288">
                          <div className={`h-full ${show.advance === 100 ? 'bg-green-500' : 'bg-primary'}`} style={{
                      width: `${show.advance}%`
                    }} data-id="element-4289"></div>
                        </div>
                        <span className="text-xs text-textMuted" data-id="element-4290">
                          {show.advance}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4291">
                      <div className="flex items-center gap-1.5" data-id="element-4292">
                        {show.lineup === 'Locked' ? <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-4293" /> : <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-4294" />}
                        <span className="text-xs" data-id="element-4295">{show.lineup}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4296">
                      <div className="flex flex-col gap-1" data-id="element-4297">
                        <div className="flex items-center gap-1.5 text-xs" data-id="element-4298">
                          <FileText className="w-3 h-3 text-textMuted" data-id="element-4299" />
                          <span className={show.contract === 'Signed' ? 'text-green-500' : 'text-textMuted'} data-id="element-4300">
                            {show.contract}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs" data-id="element-4301">
                          <DollarSign className="w-3 h-3 text-textMuted" data-id="element-4302" />
                          <span className={show.deposit === 'Paid' ? 'text-green-500' : show.deposit === 'Pending' ? 'text-amber-500' : 'text-textMuted'} data-id="element-4303">
                            {show.deposit}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Advancing Tab */}
      {activeTab === 'advancing' && <div className="flex-1 overflow-y-auto" data-id="element-4304">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4305">
            {[{
          date: 'Jun 12',
          venue: 'The Empty Bottle',
          progress: 100,
          tasks: [{
            name: 'Contacts confirmed',
            done: true
          }, {
            name: 'Load-in time set',
            done: true
          }, {
            name: 'Guest list submitted',
            done: true
          }, {
            name: 'Backline confirmed',
            done: true
          }, {
            name: 'Promo assets sent',
            done: true
          }]
        }, {
          date: 'Jun 14',
          venue: 'Beachland Tavern',
          progress: 60,
          tasks: [{
            name: 'Contacts confirmed',
            done: true
          }, {
            name: 'Load-in time set',
            done: true
          }, {
            name: 'Guest list submitted',
            done: false
          }, {
            name: 'Backline confirmed',
            done: false
          }, {
            name: 'Promo assets sent',
            done: true
          }]
        }, {
          date: 'Jun 22',
          venue: 'The Sinclair',
          progress: 20,
          tasks: [{
            name: 'Contacts confirmed',
            done: true
          }, {
            name: 'Load-in time set',
            done: false
          }, {
            name: 'Guest list submitted',
            done: false
          }, {
            name: 'Backline confirmed',
            done: false
          }, {
            name: 'Promo assets sent',
            done: false
          }]
        }].map((show, i) => <div key={i} className="bg-surface border border-border rounded-xl p-5" data-id="element-4306">
                <div className="flex justify-between items-start mb-4" data-id="element-4307">
                  <div data-id="element-4308">
                    <h3 className="font-semibold" data-id="element-4309">{show.venue}</h3>
                    <p className="text-sm text-textMuted" data-id="element-4310">{show.date}</p>
                  </div>
                  <div className="relative w-10 h-10 flex items-center justify-center" data-id="element-4311">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36" data-id="element-4312">
                      <path className="text-background" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" data-id="element-4313" />
                      <path className={show.progress === 100 ? 'text-green-500' : 'text-primary'} strokeWidth="3" strokeDasharray={`${show.progress}, 100`} stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" data-id="element-4314" />
                    </svg>
                    <span className="absolute text-[10px] font-bold" data-id="element-4315">
                      {show.progress}%
                    </span>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-4316">
                  {show.tasks.map((task, j) => <div key={j} className="flex items-start gap-3" data-id="element-4317">
                      <button className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${task.done ? 'bg-primary border-primary text-background' : 'border-border hover:border-primary'}`} data-id="element-4318">
                        {task.done && <CheckSquare className="w-3 h-3" data-id="element-4319" />}
                      </button>
                      <div className="flex-1 min-w-0" data-id="element-4320">
                        <p className={`text-sm ${task.done ? 'text-textMuted line-through' : 'text-textMain'}`} data-id="element-4321">
                          {task.name}
                        </p>
                        {!task.done && <div className="flex items-center gap-2 mt-1" data-id="element-4322">
                            <div className="w-4 h-4 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[8px] font-bold" data-id="element-4323">
                              M
                            </div>
                            <span className="text-[10px] text-red-500" data-id="element-4324">
                              Due in 2 days
                            </span>
                          </div>}
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>
        </div>}

      {/* Itinerary Tab */}
      {activeTab === 'itinerary' && <div className="flex-1 overflow-y-auto" data-id="element-4325">
          <div className="max-w-3xl mx-auto" data-id="element-4326">
            <div className="flex justify-end mb-6" data-id="element-4327">
              <button className="px-4 py-2 bg-surface border border-border rounded-lg hover:bg-surfaceHover transition-colors text-sm font-medium flex items-center gap-2" data-id="element-4328">
                <Download className="w-4 h-4" data-id="element-4329" /> Download PDF
              </button>
            </div>

            <div className="space-y-8" data-id="element-4330">
              {/* Day 1 */}
              <div data-id="element-4331">
                <div className="sticky top-0 bg-background/95 backdrop-blur py-2 z-10 mb-4 border-b border-border" data-id="element-4332">
                  <h3 className="text-lg font-bold" data-id="element-4333">Friday, June 12</h3>
                  <p className="text-sm text-textMuted" data-id="element-4334">Chicago, IL</p>
                </div>

                <div className="relative pl-8 space-y-6" data-id="element-4335">
                  <div className="absolute left-[15px] top-2 bottom-0 w-px bg-border" data-id="element-4336"></div>

                  {/* Travel */}
                  <div className="relative" data-id="element-4337">
                    <div className="absolute -left-8 w-6 h-6 rounded-full bg-surface border-2 border-border flex items-center justify-center z-10" data-id="element-4338">
                      <Car className="w-3 h-3 text-textMuted" data-id="element-4339" />
                    </div>
                    <div className="bg-surface border border-border rounded-xl p-4" data-id="element-4340">
                      <div className="flex justify-between items-start mb-2" data-id="element-4341">
                        <h4 className="font-semibold text-sm" data-id="element-4342">
                          Drive to Chicago
                        </h4>
                        <span className="text-xs text-textMuted" data-id="element-4343">
                          10:00 AM - 2:00 PM
                        </span>
                      </div>
                      <div className="text-sm text-textMuted flex items-center gap-4" data-id="element-4344">
                        <span className="flex items-center gap-1" data-id="element-4345">
                          <MapPin className="w-3 h-3" data-id="element-4346" /> 240 miles
                        </span>
                        <span className="flex items-center gap-1" data-id="element-4347">
                          <Clock className="w-3 h-3" data-id="element-4348" /> ~4 hours
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Lodging */}
                  <div className="relative" data-id="element-4349">
                    <div className="absolute -left-8 w-6 h-6 rounded-full bg-surface border-2 border-border flex items-center justify-center z-10" data-id="element-4350">
                      <Bed className="w-3 h-3 text-textMuted" data-id="element-4351" />
                    </div>
                    <div className="bg-surface border border-border rounded-xl p-4" data-id="element-4352">
                      <div className="flex justify-between items-start mb-2" data-id="element-4353">
                        <h4 className="font-semibold text-sm" data-id="element-4354">
                          Check-in: The Robey
                        </h4>
                        <span className="text-xs text-textMuted" data-id="element-4355">3:00 PM</span>
                      </div>
                      <p className="text-sm text-textMuted mb-2" data-id="element-4356">
                        2018 W North Ave, Chicago, IL 60647
                      </p>
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-background rounded text-xs font-medium border border-border" data-id="element-4357">
                        <FileText className="w-3 h-3" data-id="element-4358" /> Conf: #H-882910
                      </div>
                    </div>
                  </div>

                  {/* Show */}
                  <div className="relative" data-id="element-4359">
                    <div className="absolute -left-8 w-6 h-6 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center z-10" data-id="element-4360">
                      <MapPin className="w-3 h-3 text-primary" data-id="element-4361" />
                    </div>
                    <div className="bg-surface border border-primary/30 rounded-xl p-4 shadow-[0_0_15px_rgba(245,158,11,0.05)]" data-id="element-4362">
                      <div className="flex justify-between items-start mb-4" data-id="element-4363">
                        <div data-id="element-4364">
                          <h4 className="font-bold text-lg text-primary" data-id="element-4365">
                            The Empty Bottle
                          </h4>
                          <p className="text-sm text-textMuted" data-id="element-4366">
                            1035 N Western Ave, Chicago, IL
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-4367">
                        <div data-id="element-4368">
                          <div className="text-xs text-textMuted mb-1" data-id="element-4369">
                            Load-in
                          </div>
                          <div className="text-sm font-medium" data-id="element-4370">5:00 PM</div>
                        </div>
                        <div data-id="element-4371">
                          <div className="text-xs text-textMuted mb-1" data-id="element-4372">
                            Soundcheck
                          </div>
                          <div className="text-sm font-medium" data-id="element-4373">6:00 PM</div>
                        </div>
                        <div data-id="element-4374">
                          <div className="text-xs text-textMuted mb-1" data-id="element-4375">
                            Doors
                          </div>
                          <div className="text-sm font-medium" data-id="element-4376">8:00 PM</div>
                        </div>
                        <div data-id="element-4377">
                          <div className="text-xs text-textMuted mb-1" data-id="element-4378">
                            Set Time
                          </div>
                          <div className="text-sm font-bold text-primary" data-id="element-4379">
                            10:00 PM
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </motion.div>;
}